import React from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

import { Box, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { keyframes } from '@emotion/react';
import { Link as RouterLink } from 'react-router-dom';

import depotImage from '../../assets/homePage/depot.jpg';
import slide2Image from '../../assets/homePage/slideshow1.PNG'; // Archives / history image
import slide3Image from '../../assets/homePage/slideshow2.JPG'; // Visit / teachers image

// NAVY palette
const NAVY = '#002F6C';
const NAVY_DARK = '#001F4C';
const GOLD = '#FFC94D';
const GOLD_DARK = '#E6B84A';

// fade-in keyframes
const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

/**
 * Slides:
 * 1) Depot (Welcome)
 * 2) Visit (teachers)
 * 3) Archives / Local history
 */
const slides = [
    {
        title: 'Welcome to the Southern Railroad Depot Museum!',
        subtitle: '',
        intro: null,
        ctaText: 'Learn More',
        ctaHref: '/about',
        image: depotImage,
    },
    {
        title: 'Bring Learning to Life',
        subtitle: 'Visit the Museum',
        intro: (
            <>
                Turn a classroom lesson into an unforgettable adventure! Guided tours,
                kid-friendly activities, and real depot historical artifacts make our museum
                a great choice for school field trips.
            </>
        ),
        ctaText: 'Plan a Visit',
        ctaHref: '/visit',
        image: slide3Image, // Visit/teachers
    },
    {
        title: 'Rediscover Piedmont’s Past',
        subtitle: 'Local History Starts Here',
        intro: (
            <>
                Celebrate our community’s legacy with historic images, recollected tales,
                and the voices of Piedmont’s pioneers.
            </>
        ),
        ctaText: 'Explore Archives',
        ctaHref: '/archives',
        image: slide2Image, // Archives / history
    },
];

export default function Hero({ height = '45vh' }) {
    const theme = useTheme();
    const upMd = useMediaQuery(theme.breakpoints.up('md'));
    const upSm = useMediaQuery(theme.breakpoints.up('sm'));

    // Fixed height on phones so images never jump as text changes
    const slideHeight = upMd ? height : (upSm ? '60vh' : '68vh');

    // Desktop: soft navy → transparent gradient over the image side
    const bgWithDesktopFade = (url) => ({
        backgroundImage: {
            xs: `url(${url})`,
            md: `
        linear-gradient(
          to right,
          ${NAVY} 0%,
          rgba(0,0,0,0) 20%
        ),
        url(${url})
      `,
        },
    });

    return (
        <Box
            component="section"
            role="region"
            aria-roledescription="carousel"
            aria-label="Homepage hero carousel"
            sx={{
                position: 'relative',
                mb: 6,
                opacity: 0,
                animation: `${fadeIn} 1s ease-out forwards`,
                // Swiper pagination: visible on navy
                '.swiper-pagination-bullet': {
                    backgroundColor: '#fff',
                    opacity: 0.45,
                    width: 8,
                    height: 8,
                    [theme.breakpoints.down('sm')]: { width: 10, height: 10 },
                },
                '.swiper-pagination-bullet-active': { opacity: 1, backgroundColor: '#fff' },
            }}
        >
            <Swiper
                modules={[Autoplay, EffectFade, Pagination]}
                effect="fade"
                autoplay={{ delay: 6000, disableOnInteraction: false }}
                fadeEffect={{ crossFade: true }}
                loop
                pagination={{
                    clickable: true,
                    renderBullet: (index, className) =>
                        `<button class="${className}" aria-label="Go to slide ${index + 1}"></button>`,
                }}
                style={{ width: '100%', height: slideHeight }}
            >
                {slides.map((slide, idx) => (
                    <SwiperSlide
                        key={idx}
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`Slide ${idx + 1} of ${slides.length}`}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: 'stretch',
                                width: '100%',
                                height: slideHeight,
                                bgcolor: NAVY, // ⬅️ navy background instead of gray
                            }}
                        >
                            {/* Text column */}
                            <Box
                                sx={{
                                    flex: { md: '1 1 50%' },
                                    px: { xs: 2, sm: 3, md: 4 },
                                    py: { xs: 2, sm: 3, md: 0 },
                                    zIndex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    gap: { xs: 2, sm: 2.5, md: 0 },
                                    height: { md: '100%' },

                                    // On small screens, slightly translucent navy card for readability
                                    [theme.breakpoints.down('md')]: {
                                        backgroundColor: 'rgba(0, 31, 76, 0.5)',
                                        backdropFilter: 'saturate(1.05) blur(2px)',
                                        borderTopLeftRadius: 12,
                                        borderTopRightRadius: 12,
                                    },
                                }}
                            >
                                <Box sx={{ mt: { xs: 1, md: 4 } }}>
                                    <Typography
                                        variant="h2"
                                        component="h1"
                                        gutterBottom
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 700,
                                            [theme.breakpoints.down('sm')]: { fontSize: '1.75rem', lineHeight: 1.2 },
                                            [theme.breakpoints.between('sm', 'md')]: { fontSize: '2rem', lineHeight: 1.25 },
                                        }}
                                    >
                                        {slide.title}
                                    </Typography>

                                    {slide.subtitle && (
                                        <Typography
                                            variant={idx === 0 ? 'h4' : 'h5'}
                                            gutterBottom
                                            sx={{
                                                color: 'rgba(255,255,255,0.92)',
                                                fontWeight: 600,
                                                [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
                                                [theme.breakpoints.between('sm', 'md')]: { fontSize: '1.25rem' },
                                            }}
                                        >
                                            {slide.subtitle}
                                        </Typography>
                                    )}

                                    {slide.intro && (
                                        <Typography
                                            variant="body1"
                                            gutterBottom
                                            sx={{
                                                mt: 1,
                                                color: 'rgba(255,255,255,0.88)',
                                                [theme.breakpoints.down('sm')]: { fontSize: '0.95rem' },
                                                [theme.breakpoints.between('sm', 'md')]: { fontSize: '1rem' },
                                            }}
                                        >
                                            {slide.intro}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ mb: { xs: 1, md: 2 } }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        component={RouterLink}
                                        to={slide.ctaHref}
                                        startIcon={<ArrowForwardIosIcon />}
                                        sx={{
                                            px: 4,
                                            py: 1.5,
                                            fontSize: '1rem',
                                            textTransform: 'none',
                                            boxShadow: theme.shadows[3],
                                            bgcolor: GOLD,             // ⬅️ gold button on navy
                                            color: '#0B1A2E',
                                            width: { xs: '100%', sm: 'auto' },
                                            '&:hover': { bgcolor: GOLD_DARK, boxShadow: theme.shadows[6] },
                                        }}
                                    >
                                        {slide.ctaText}
                                    </Button>
                                </Box>
                            </Box>

                            {/* Image column */}
                            <Box
                                sx={{
                                    flex: { md: '1 1 50%' },
                                    height: { xs: '40vh', sm: '45vh', md: '100%' },
                                    ...bgWithDesktopFade(slide.image),
                                    backgroundSize: 'cover',
                                    backgroundPosition: { xs: 'center', md: 'center right' },
                                    backgroundRepeat: 'no-repeat',
                                    borderRadius: { xs: 2, md: 0 },
                                }}
                            />
                        </Box>
                    </SwiperSlide>
                ))}
            </Swiper>
        </Box>
    );
}

Hero.propTypes = {
    /** CSS height for the hero carousel (e.g. "65vh", "600px") */
    height: PropTypes.string,
};
